/**
 * Molecule · Brand badge — choosing text that stays readable on a colour we do
 * not control.
 *
 * A creator picks their own accent and it is used as a button background. A
 * pale one leaves white text at about 1.6:1, which is invisible. This is a port
 * of the extension's utils/color.ts, threshold included, so a given accent
 * resolves to the same text colour on the site as it does in the player.
 */

/**
 * Luminance at which dark text overtakes white against the same background.
 * Derived by solving the two WCAG contrast ratios for equality; anything
 * brighter reads better with dark text.
 */
const DARK_TEXT_ABOVE = 0.235;

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const RGB = /^rgba?\(([^)]+)\)$/i;

function channel(value: number): number {
  const v = value / 255;
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance, or null when the colour cannot be read. */
function relativeLuminance(colour: string): number | null {
  const value = colour.trim();

  const hex = HEX.exec(value);
  if (hex) {
    const digits = hex[1].length === 3
      ? hex[1].split('').map((d) => d + d).join('')
      : hex[1];
    const parts = [0, 2, 4].map((i) => parseInt(digits.slice(i, i + 2), 16));
    return 0.2126 * channel(parts[0]) + 0.7152 * channel(parts[1]) + 0.0722 * channel(parts[2]);
  }

  // A computed custom property comes back as rgb() as often as hex.
  const rgb = RGB.exec(value);
  if (rgb) {
    const parts = rgb[1].split(/[\s,/]+/).filter(Boolean).slice(0, 3).map(Number);
    if (parts.length === 3 && parts.every((n) => Number.isFinite(n))) {
      return 0.2126 * channel(parts[0]) + 0.7152 * channel(parts[1]) + 0.0722 * channel(parts[2]);
    }
  }

  return null;
}

/** What to write on top of `background`. Unreadable input falls back to white. */
export function textOn(background: string): string {
  const luminance = relativeLuminance(background);
  return luminance !== null && luminance > DARK_TEXT_ABOVE
    ? 'var(--fc-ink)'
    : 'var(--fc-neutral-0)';
}

/**
 * Derives `--fc-creator-on-accent` wherever `--fc-creator-accent` is set, so a
 * page only ever has to name the accent. Setting one without the other is what
 * puts white text on a yellow button.
 */
export function initCreatorAccent(scope: ParentNode = document): void {
  scope.querySelectorAll<HTMLElement>('[style*="--fc-creator-accent"]').forEach((el) => {
    const accent = getComputedStyle(el).getPropertyValue('--fc-creator-accent');
    if (!accent.trim()) return;
    el.style.setProperty('--fc-creator-on-accent', textOn(accent));
  });
}
